import { useRef, useState, useEffect } from "react"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"
import CustomImage from "components/atoms/Image/CustomImage"
import { useLang } from "hooks/useLang"
import { IoMdClose } from "react-icons/io"
import { motion } from "framer-motion"
import useClickOutside from "hooks/useClickOutside"

interface IProps {
    images: string[]
    close: () => void
    clickedIndex: number
    withRoot?: boolean
}

const GallerySlider = ({
    images,
    close,
    clickedIndex,
    withRoot = true,
}: IProps) => {
    const [currentIndex, setCurrentIndex] = useState(clickedIndex)
    const { isAr } = useLang()
    const oneImage = images.length === 1

    const ref = useRef<HTMLDivElement>(null)

    useClickOutside(ref, close)

    useEffect(() => {
        // Save the current overflow value
        const originalStyle = window.getComputedStyle(document.body).overflow
        // Disable scroll
        document.body.style.overflow = "hidden"

        // Re-enable scroll on cleanup
        return () => {
            document.body.style.overflow = originalStyle
        }
    }, [])

    const changeSlide = (nextIndex: number) => {
        setCurrentIndex(nextIndex)
    }

    const nextSlide = () => {
        const nextIndex = (currentIndex + 1) % images.length
        changeSlide(nextIndex)
    }

    const prevSlide = () => {
        const nextIndex = (currentIndex - 1 + images.length) % images.length
        changeSlide(nextIndex)
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed left-0 top-0 !z-[10000] grid h-full w-full place-content-center bg-primary-theme-500 bg-opacity-50 backdrop-blur-md"
        >
            <div
                ref={ref}
                className="slider-container relative h-full w-full overflow-hidden rounded-xl"
                style={{
                    transition: "opacity 0.5s ease-in-out",
                }}
            >
                {!oneImage && (
                    <div className="fixed left-0 top-1/2 z-20 flex w-full -translate-y-1/2 items-center justify-between px-0">
                        <FiChevronLeft
                            className={`slider-button ${
                                isAr ? "rotate-180" : "rotate-0"
                            }`}
                            onClick={prevSlide}
                        />
                        <FiChevronRight
                            className={`slider-button ${
                                isAr ? "rotate-180" : "rotate-0"
                            }`}
                            onClick={nextSlide}
                        />
                    </div>
                )}

                <div className="relative mx-auto flex h-full w-full items-center justify-center">
                    <div
                        className="relative"
                        style={{ width: "90%", height: "80%" }}
                    >
                        <IoMdClose
                            className="absolute -right-5 -top-5 z-50 h-8 w-8 cursor-pointer rounded-full bg-black bg-opacity-50 p-1.5 text-white transition-all hover:bg-opacity-70 md:h-10 md:w-10"
                            onClick={close}
                        />
                        <CustomImage
                            src={
                                withRoot
                                    ? process.env.NEXT_PUBLIC_Event_Img_URL +
                                      images[currentIndex]
                                    : images[currentIndex]
                            }
                            alt={`mini-img-${images[currentIndex]}`}
                            style={{
                                objectFit: "contain",
                                maxHeight: "80vh",
                                width: "auto",
                            }}
                            width={1200}
                            height={800}
                            className="slider-image mx-auto select-none"
                        />
                    </div>
                </div>

                {!oneImage && (
                    <div className="absolute bottom-10 left-1/2 flex -translate-x-1/2 items-center justify-center gap-2">
                        {images.map((_, idx) => (
                            <span
                                key={idx}
                                onClick={() => changeSlide(idx)}
                                className={`dot h-2 cursor-pointer rounded-full transition-all md:h-3 ${
                                    currentIndex === idx
                                        ? "active w-7 bg-primary-theme-200 dark:bg-action-theme-200 md:w-10"
                                        : "w-2 bg-gray-100 dark:bg-gray-200 md:w-3"
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    )
}

export default GallerySlider
